import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Calendar, Package, Star, TrendingUp, CheckCircle, ShoppingBag, Settings, LogOut, RefreshCw, Mail, Send, MailCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    completed: 'bg-blue-50 text-blue-700 border-blue-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
    processing: 'bg-[var(--color-sage-soft)] text-[var(--color-brand)] border-[rgba(61,114,88,0.3)]'
  };
  const labels = { pending: 'In attesa', confirmed: 'Confermato', completed: 'Completato', cancelled: 'Annullato', processing: 'In lavorazione' };
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${styles[status] || styles.pending}`}>
      {labels[status] || status}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color = 'sage', loading }) => (
  <Card className="bg-[var(--color-paper)] border-[var(--color-border)] rounded-2xl shadow-sm">
    <CardContent className="p-5">
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-4 w-24" />
        </div>
      ) : (
        <>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${
            color === 'terracotta' ? 'bg-[var(--color-terracotta-soft)]' : 'bg-[var(--color-sage-soft)]'
          }`}>
            <Icon size={22} className={color === 'terracotta' ? 'text-[var(--color-terracotta)]' : 'text-[var(--color-brand)]'} />
          </div>
          <div className="font-serif text-3xl font-semibold text-[var(--color-deep)]" data-testid={`kpi-${label.toLowerCase().replace(/ /g, '-')}`}>{value}</div>
          <div className="text-sm text-[var(--color-muted-text)] mt-0.5">{label}</div>
          {sub && <div className="text-xs text-[var(--color-terracotta)] font-medium mt-1">{sub}</div>}
        </>
      )}
    </CardContent>
  </Card>
);

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  // Newsletter
  const [subscribers, setSubscribers] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingMonthly, setSendingMonthly] = useState(false);

  const fetchStats    = useCallback(async () => { setLoadingStats(true);    try { const r = await api.get('/admin/stats');   setStats(r.data); }           catch { toast.error('Errore statistiche'); }           finally { setLoadingStats(false); } }, []);
  const fetchBookings = useCallback(async () => { setLoadingBookings(true); try { const r = await api.get('/bookings');         setBookings(r.data.items || []); } catch {} finally { setLoadingBookings(false); } }, []);
  const fetchOrders   = useCallback(async () => { setLoadingOrders(true);   try { const r = await api.get('/orders');           setOrders(r.data.items || []);   } catch {} finally { setLoadingOrders(false);   } }, []);
  const fetchUsers    = useCallback(async () => { setLoadingUsers(true);    try { const r = await api.get('/admin/users');     setUsers(r.data.items || []);    } catch {} finally { setLoadingUsers(false);    } }, []);
  const fetchSubs     = useCallback(async () => { setLoadingSubs(true);     try { const r = await api.get('/newsletter/subscribers'); setSubscribers(r.data.items || []); } catch {} finally { setLoadingSubs(false); } }, []);

  useEffect(() => { fetchStats(); fetchBookings(); fetchOrders(); fetchUsers(); }, [fetchStats, fetchBookings, fetchOrders, fetchUsers]);

  const sendTestEmail = async () => {
    if (!testEmail.trim()) return;
    setSendingTest(true);
    try {
      const r = await api.post('/newsletter/send-test', { to_email: testEmail });
      toast.success(r.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio email di test');
    } finally { setSendingTest(false); }
  };

  const sendMonthlyNewsletter = async () => {
    if (!window.confirm('Inviare la newsletter mensile a tutti gli iscritti attivi?')) return;
    setSendingMonthly(true);
    try {
      const r = await api.post('/newsletter/send-monthly');
      toast.success(r.data.message);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio newsletter');
    } finally { setSendingMonthly(false); }
  };

  const updateBookingStatus = async (id, status) => {
    try { await api.put(`/bookings/${id}`, { status }); setBookings(p => p.map(b => b.id === id ? {...b, status} : b)); toast.success('Stato aggiornato'); fetchStats(); }
    catch { toast.error('Errore aggiornamento'); }
  };
  const updateOrderStatus = async (id, status) => {
    try { await api.put(`/orders/${id}`, { status }); setOrders(p => p.map(o => o.id === id ? {...o, status} : o)); toast.success('Stato aggiornato'); fetchStats(); }
    catch { toast.error('Errore aggiornamento'); }
  };
  const toggleUser = async (userId) => {
    try { const r = await api.put(`/admin/users/${userId}/toggle`); setUsers(p => p.map(u => u.id === userId ? {...u, is_active: r.data.is_active} : u)); toast.success('Utente aggiornato'); }
    catch { toast.error('Errore aggiornamento'); }
  };

  return (
    <div className="min-h-screen bg-[var(--color-cream)]" data-testid="admin-dashboard">

      {/* Admin header con logo intero */}
      <div style={{ backgroundColor: '#2D5D42' }} className="text-white py-4 border-b border-white/10">
        <div className="container-custom flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Logo completo — h fissa, w automatica */}
            <img
              src="/logo.png"
              alt="Una Zampa in Paradiso"
              className="h-12 w-auto object-contain rounded-lg"
            />
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#F5F5DC]/70">Pannello Amministratore &middot; v1.0.0</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchStats} className="text-white/70 hover:text-white hover:bg-white/10 h-8 w-8 p-0" data-testid="admin-refresh-button">
              <RefreshCw size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} className="text-white/70 hover:text-white hover:bg-white/10 gap-1.5 h-8" data-testid="admin-logout-button">
              <LogOut size={14} />
              <span className="hidden sm:inline text-xs">Esci</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard icon={Users}       label="Utenti"         value={stats?.users ?? '-'}     loading={loadingStats} />
          <KpiCard icon={Calendar}    label="Prenotazioni"   value={stats?.bookings ?? '-'}  sub={stats?.pending_bookings ? `${stats.pending_bookings} in attesa` : null} loading={loadingStats} color="terracotta" />
          <KpiCard icon={ShoppingBag} label="Ordini"         value={stats?.orders ?? '-'}    sub={stats?.pending_orders ? `${stats.pending_orders} in attesa` : null} loading={loadingStats} />
          <KpiCard icon={Star}        label="Memorial"       value={stats?.memorials ?? '-'} loading={loadingStats} color="terracotta" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard icon={Package}     label="Prodotti"       value={stats?.products ?? '-'}        loading={loadingStats} />
          <KpiCard icon={Settings}    label="Servizi"        value={stats?.services ?? '-'}        loading={loadingStats} color="terracotta" />
          <KpiCard icon={TrendingUp}  label="Post Community" value={stats?.community_posts ?? '-'} loading={loadingStats} />
          <KpiCard icon={Mail}        label="Newsletter"     value={stats?.newsletter_subscribers ?? '-'} loading={loadingStats} color="terracotta" />
        </div>

        {/* Management Tabs */}
        <Tabs defaultValue="bookings">
          <TabsList className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-xl p-1 mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="bookings"    className="rounded-lg data-[state=active]:bg-[var(--color-brand)] data-[state=active]:text-white" data-testid="admin-tab-bookings">Prenotazioni</TabsTrigger>
            <TabsTrigger value="orders"      className="rounded-lg data-[state=active]:bg-[var(--color-brand)] data-[state=active]:text-white" data-testid="admin-tab-orders">Ordini</TabsTrigger>
            <TabsTrigger value="users"       className="rounded-lg data-[state=active]:bg-[var(--color-brand)] data-[state=active]:text-white" data-testid="admin-tab-users">Utenti</TabsTrigger>
            <TabsTrigger value="newsletter"  className="rounded-lg data-[state=active]:bg-[var(--color-brand)] data-[state=active]:text-white" data-testid="admin-tab-newsletter" onClick={fetchSubs}>Newsletter</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden">
              <div className="p-5 border-b border-[var(--color-border)]">
                <h3 className="font-serif text-lg font-semibold text-[var(--color-deep)]">Gestione Prenotazioni</h3>
              </div>
              {loadingBookings ? (
                <div className="p-5 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
              ) : bookings.length === 0 ? (
                <div className="text-center py-12 text-[var(--color-muted-text)]">
                  <Calendar size={36} className="mx-auto mb-3 opacity-40" /><p>Nessuna prenotazione</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[var(--color-border)]">
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Servizio</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Animale</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Contatto</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Data</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Stato</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map(b => (
                        <TableRow key={b.id} className="border-[var(--color-border)] hover:bg-[var(--color-cream)]/60" data-testid={`admin-booking-row-${b.id}`}>
                          <TableCell className="font-medium text-sm text-[var(--color-deep)] max-w-[160px] truncate">{b.service_name_it}</TableCell>
                          <TableCell className="text-sm text-[var(--color-muted-text)]">{b.pet_name} ({b.pet_species})</TableCell>
                          <TableCell className="text-sm text-[var(--color-muted-text)] max-w-[140px] truncate">{b.contact_name}</TableCell>
                          <TableCell className="text-xs text-[var(--color-stone)]">{new Date(b.created_at).toLocaleDateString('it-IT')}</TableCell>
                          <TableCell><StatusBadge status={b.status} /></TableCell>
                          <TableCell>
                            <Select value={b.status} onValueChange={(v) => updateBookingStatus(b.id, v)}>
                              <SelectTrigger className="h-8 text-xs rounded-lg border-[var(--color-border)] w-36" data-testid={`booking-status-select-${b.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">In attesa</SelectItem>
                                <SelectItem value="confirmed">Confermato</SelectItem>
                                <SelectItem value="processing">In lavorazione</SelectItem>
                                <SelectItem value="completed">Completato</SelectItem>
                                <SelectItem value="cancelled">Annullato</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden">
              <div className="p-5 border-b border-[var(--color-border)]">
                <h3 className="font-serif text-lg font-semibold text-[var(--color-deep)]">Gestione Ordini</h3>
              </div>
              {loadingOrders ? (
                <div className="p-5 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-[var(--color-muted-text)]">
                  <ShoppingBag size={36} className="mx-auto mb-3 opacity-40" /><p>Nessun ordine</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[var(--color-border)]">
                        <TableHead className="text-xs text-[var(--color-muted-text)]">ID Ordine</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Prodotti</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Cliente</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Data</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Stato</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map(o => (
                        <TableRow key={o.id} className="border-[var(--color-border)] hover:bg-[var(--color-cream)]/60" data-testid={`admin-order-row-${o.id}`}>
                          <TableCell className="text-xs font-mono text-[var(--color-muted-text)]">{o.id.slice(0,8)}...</TableCell>
                          <TableCell className="text-sm text-[var(--color-muted-text)] max-w-[180px]"><div className="truncate">{Array.isArray(o.items) && o.items.map((item,i) => <span key={i}>{item.product_name}{i < o.items.length-1 ? ', ' : ''}</span>)}</div></TableCell>
                          <TableCell className="text-sm text-[var(--color-muted-text)] max-w-[140px] truncate">{o.contact_name}</TableCell>
                          <TableCell className="text-xs text-[var(--color-stone)]">{new Date(o.created_at).toLocaleDateString('it-IT')}</TableCell>
                          <TableCell><StatusBadge status={o.status} /></TableCell>
                          <TableCell>
                            <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.id, v)}>
                              <SelectTrigger className="h-8 text-xs rounded-lg border-[var(--color-border)] w-36" data-testid={`order-status-select-${o.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">In attesa</SelectItem>
                                <SelectItem value="confirmed">Confermato</SelectItem>
                                <SelectItem value="processing">In lavorazione</SelectItem>
                                <SelectItem value="completed">Completato</SelectItem>
                                <SelectItem value="cancelled">Annullato</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden" data-testid="admin-users-table">
              <div className="p-5 border-b border-[var(--color-border)]">
                <h3 className="font-serif text-lg font-semibold text-[var(--color-deep)]">Gestione Utenti</h3>
              </div>
              {loadingUsers ? (
                <div className="p-5 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[var(--color-border)]">
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Nome</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Email</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Ruolo</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Citt&agrave;</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Stato</TableHead>
                        <TableHead className="text-xs text-[var(--color-muted-text)]">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(u => (
                        <TableRow key={u.id} className="border-[var(--color-border)] hover:bg-[var(--color-cream)]/60" data-testid={`admin-user-row-${u.id}`}>
                          <TableCell className="font-medium text-sm text-[var(--color-deep)]">{u.first_name} {u.last_name}</TableCell>
                          <TableCell className="text-sm text-[var(--color-muted-text)]">{u.email}</TableCell>
                          <TableCell>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${ u.role === 'admin' ? 'bg-[var(--color-terracotta-soft)] text-[var(--color-terracotta)] border-[rgba(139,74,60,0.2)]' : 'bg-[var(--color-sage-soft)] text-[var(--color-brand)] border-[rgba(61,114,88,0.2)]' }`}>
                              {u.role}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-[var(--color-muted-text)]">{u.city || '-'}</TableCell>
                          <TableCell>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${ u.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200' }`}>
                              {u.is_active ? 'Attivo' : 'Disabilitato'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {u.role !== 'admin' && (
                              <Button variant="ghost" size="sm" onClick={() => toggleUser(u.id)}
                                className={`text-xs h-8 rounded-lg ${ u.is_active ? 'text-red-600 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50' }`}
                                data-testid={`toggle-user-${u.id}`}
                              >
                                {u.is_active ? 'Disabilita' : 'Riabilita'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─────── NEWSLETTER TAB ─────── */}
          <TabsContent value="newsletter">
            <div className="space-y-6">

              {/* Azioni */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Invio email di test */}
                <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-6" data-testid="newsletter-test-panel">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-[var(--color-sage-soft)] flex items-center justify-center">
                      <Send size={16} className="text-[var(--color-brand)]" />
                    </div>
                    <h3 className="font-serif text-base font-semibold text-[var(--color-deep)]">Email di test</h3>
                  </div>
                  <p className="text-sm text-[var(--color-muted-text)] mb-4">Invia un'anteprima della newsletter mensile a un indirizzo specifico per verificare il template.</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="email@esempio.it"
                      value={testEmail}
                      onChange={e => setTestEmail(e.target.value)}
                      className="flex-1 border border-[var(--color-border)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-sage)]"
                      data-testid="admin-test-email-input"
                    />
                    <Button
                      onClick={sendTestEmail}
                      disabled={sendingTest || !testEmail.trim()}
                      className="bg-[var(--color-brand)] hover:bg-[var(--color-deep)] text-white rounded-xl px-4 text-sm"
                      data-testid="admin-send-test-btn"
                    >
                      {sendingTest ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Send size={14} />}
                    </Button>
                  </div>
                </div>

                {/* Invio newsletter mensile */}
                <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-6" data-testid="newsletter-monthly-panel">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-[var(--color-terracotta-soft)] flex items-center justify-center">
                      <MailCheck size={16} className="text-[var(--color-terracotta)]" />
                    </div>
                    <h3 className="font-serif text-base font-semibold text-[var(--color-deep)]">Newsletter mensile</h3>
                  </div>
                  <p className="text-sm text-[var(--color-muted-text)] mb-2">
                    Invia la newsletter "Notizie dal Paradiso" a tutti gli iscritti attivi.
                  </p>
                  <p className="text-xs text-[var(--color-terracotta)] font-medium mb-4">
                    {stats?.newsletter_subscribers ?? 0} iscritti attivi riceveranno l'email
                  </p>
                  <Button
                    onClick={sendMonthlyNewsletter}
                    disabled={sendingMonthly || !stats?.newsletter_subscribers}
                    className="w-full bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl text-sm"
                    data-testid="admin-send-monthly-btn"
                  >
                    {sendingMonthly ? (
                      <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Invio in corso…</span>
                    ) : (
                      <span className="flex items-center gap-2"><Mail size={14} /> Invia newsletter mensile</span>
                    )}
                  </Button>
                </div>
              </div>

              {/* Lista iscritti */}
              <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden" data-testid="newsletter-subscribers-table">
                <div className="p-5 border-b border-[var(--color-border)] flex items-center justify-between">
                  <h3 className="font-serif text-lg font-semibold text-[var(--color-deep)]">
                    Iscritti alla newsletter
                    <span className="ml-2 text-sm font-normal text-[var(--color-muted-text)]">({stats?.newsletter_subscribers ?? 0} attivi)</span>
                  </h3>
                  <Button variant="ghost" size="sm" onClick={fetchSubs} className="text-[var(--color-muted-text)] hover:text-[var(--color-deep)]">
                    <RefreshCw size={14} />
                  </Button>
                </div>
                {loadingSubs ? (
                  <div className="p-5 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 rounded-xl" />)}</div>
                ) : subscribers.length === 0 ? (
                  <div className="p-10 text-center text-[var(--color-muted-text)]">
                    <Mail size={32} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Nessun iscritto ancora. Il form è nel footer del sito!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[var(--color-border)]">
                          <TableHead className="text-xs text-[var(--color-muted-text)]">Nome</TableHead>
                          <TableHead className="text-xs text-[var(--color-muted-text)]">Email</TableHead>
                          <TableHead className="text-xs text-[var(--color-muted-text)]">Data iscrizione</TableHead>
                          <TableHead className="text-xs text-[var(--color-muted-text)]">Ultima email</TableHead>
                          <TableHead className="text-xs text-[var(--color-muted-text)]">Stato</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subscribers.map(s => (
                          <TableRow key={s.id} className="border-[var(--color-border)] hover:bg-[var(--color-cream)]/60">
                            <TableCell className="text-sm text-[var(--color-deep)]">{s.first_name || '—'}</TableCell>
                            <TableCell className="text-sm text-[var(--color-muted-text)]">{s.email}</TableCell>
                            <TableCell className="text-xs text-[var(--color-muted-text)]">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString('it-IT') : '—'}</TableCell>
                            <TableCell className="text-xs text-[var(--color-muted-text)]">{s.last_email_sent_at ? new Date(s.last_email_sent_at).toLocaleDateString('it-IT') : 'Mai'}</TableCell>
                            <TableCell>
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${s.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                                {s.is_active ? 'Attivo' : 'Cancellato'}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}
